import { Anchor, Text } from "@mantine/core";
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useRef, useState, type ReactNode } from "react";

import { ApiError } from "../../api/errors";
import { saveJobOutput, useSaveJobOutput } from "../../api/generated/endpoints/assets/assets";
import type { Asset, SaveRefusal, SaveRequest } from "../../api/generated/model";
import { AssetNotSavableDetails } from "../../api/generated/zod";
import { refreshLibraryLists } from "../../api/library-lists";
import {
  attemptKey,
  lostAttempt,
  useIdempotencyKey,
  type LostAttempt,
} from "../idempotency";
import { toUserMessage } from "../messages";
import { patchListedJobs } from "./job-list";
import { outputKey, type OutputPick } from "./output-picks";

const REFUSAL: Record<SaveRefusal, string> = {
  not_delivered: "This output was not delivered, so there is nothing to save.",
  unsupported_type: "The library does not store this type of file.",
  expired: "This result is no longer kept, so it cannot be saved.",
  // Live's words: the Lip-Sync form's speech is its lip-sync's input (#514).
  generation_input: "This audio belongs to a generation and cannot be saved",
};

/** Why a save failed, in words: the refusal's reason, or the shell's mapping. */
function problemOf(error: unknown): string {
  if (error instanceof ApiError && error.code === "ASSET_NOT_SAVABLE") {
    const details = AssetNotSavableDetails.safeParse(error.details);
    if (details.success) return REFUSAL[details.data.reason];
  }
  return toUserMessage(error);
}

/**
 * The listed jobs' outputs these copies were saved from now name them (`saved_asset_id`, G12), as
 * the next read of the list will: a card shows as saved, and offers Save Prompt, at once.
 */
function markSaved(queryClient: QueryClient, teamId: string, assets: readonly Asset[]) {
  const copies = new Map(
    assets.flatMap((asset) =>
      asset.origin === null
        ? []
        : [[outputKey({ jobId: asset.origin.job_id, index: asset.origin.output_index }), asset.id]],
    ),
  );
  if (copies.size === 0) return;
  patchListedJobs(queryClient, teamId, (job) => {
    const copyOf = (index: number) => copies.get(outputKey({ jobId: job.id, index }));
    if (!job.outputs.some((output) => copyOf(output.index) !== undefined)) return job;
    const outputs = job.outputs.map((output) => {
      const copy = copyOf(output.index);
      return copy === undefined ? output : { ...output, saved_asset_id: copy };
    });
    return { ...job, outputs };
  });
}

export interface SaveOutputProps {
  teamId: string;
  brandId: string;
  jobId: string;
  index: number;
  /** The copy the save answered (`pending` while the server copies it), for a form that takes it. */
  onSaved?: ((asset: Asset) => void) | undefined;
}

/**
 * Keeps one delivered output in the brand's library (S1). The server copies the file in the
 * background and answers at once, and a second save of the same output returns the same asset.
 * The library's lists are read again, and while the copy runs the link names the asset, so the
 * library shows it as saving until it is ready (its list holds ready files only). `result` is
 * what to show once asked: the saved line with its link, or why it could not be saved.
 */
export function useSaveOutput({ teamId, brandId, jobId, index, onSaved }: SaveOutputProps) {
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const save = useSaveJobOutput({
    mutation: {
      onSuccess: (asset) => {
        markSaved(queryClient, teamId, [asset]);
        onSaved?.(asset);
        return refreshLibraryLists(queryClient, teamId, brandId);
      },
    },
  });
  const onSave = () => {
    const data = { job_id: jobId, output_index: index };
    const key = idempotency.keyFor(data);
    save.mutate(
      { teamId, brandId, data, headers: { "Idempotency-Key": key } },
      { onError: (error) => idempotency.failed(key, data, error) },
    );
  };
  let result: ReactNode = null;
  if (save.isSuccess) {
    result = <SavedLine teamId={teamId} brandId={brandId} assets={[save.data]} folderId={null} />;
  } else if (save.isError) {
    result = (
      <Text size="sm" role="alert">
        {problemOf(save.error)}
      </Text>
    );
  }
  return { save: onSave, saving: save.isPending, saved: save.isSuccess, result };
}

/**
 * Where saved outputs went: "Saved to the library" (or the project chosen), its link opening
 * the library at the project, or at the copy still being made. An output saved before stays
 * where it is (a save never moves a copy), and the line says so.
 */
function SavedLine(props: {
  teamId: string;
  brandId: string;
  assets: readonly Asset[];
  folderId: string | null;
}) {
  const { teamId, brandId, assets, folderId } = props;
  const copying = assets.find((asset) => asset.status !== "ready");
  const elsewhere = folderId !== null && assets.some((asset) => asset.folder_id !== folderId);
  let search: { folder?: string; saving?: string } = {};
  if (folderId !== null) search = { folder: folderId };
  else if (copying !== undefined) search = { saving: copying.id };
  return (
    <Text component="output" size="sm">
      Saved to the{" "}
      <Anchor
        c="inherit"
        underline="always"
        renderRoot={(rootProps) => (
          <Link
            {...rootProps}
            to="/teams/$teamId/brands/$brandId/library"
            params={{ teamId, brandId }}
            search={search}
          />
        )}
      >
        {folderId === null ? "library" : "project"}
      </Anchor>
      {copying === undefined ? "." : "; it appears there in a moment."}
      {elsewhere ? " One was saved before, so it stays where it was." : null}
    </Text>
  );
}

/** Where and how a save keeps its outputs. */
export interface SaveOptions {
  /** The project to keep them in; the library's root by default. */
  folderId?: string | null;
  /** The name typed (live's Sounds "Add name"); the server makes it a file name. */
  name?: string;
  /** After every output is kept, with the library's files that keep them. */
  onSaved?: (assets: readonly Asset[]) => void;
  /** After a refusal (`result` says why). */
  onFailed?: () => void;
}

/**
 * Keeps several delivered outputs of a row, of one job or of a talking head's jobs: live's "Save
 * generation" (every one), and its "Save to project" with the project chosen. One save per
 * output, each with its own Idempotency-Key (a lost answer keeps its key, an output at a time); a
 * refusal stops the rest and is the result. An output saved before returns its copy where it is
 * (assets AGENTS.md). Outputs are named by job and index (`outputKey`), as sibling jobs each have
 * an output 0.
 */
export function useSaveOutputs({ teamId, brandId }: Pick<SaveOutputProps, "teamId" | "brandId">) {
  const queryClient = useQueryClient();
  const lost = useRef(new Map<string, LostAttempt>());
  // The outputs kept so far, by `outputKey`, so their cards show as saved.
  const [saved, setSaved] = useState<ReadonlySet<string>>(new Set());
  const save = useMutation({
    mutationFn: async (asked: {
      picks: readonly OutputPick[];
      folderId: string | null;
      name: string | null;
    }) => {
      const assets: Asset[] = [];
      for (const pick of asked.picks) {
        const data: SaveRequest = { job_id: pick.jobId, output_index: pick.index };
        if (asked.folderId !== null) data.folder_id = asked.folderId;
        if (asked.name !== null) data.name = asked.name;
        const named = outputKey(pick);
        const key = attemptKey(data, lost.current.get(named));
        lost.current.delete(named);
        try {
          assets.push(await saveJobOutput(teamId, brandId, data, { "Idempotency-Key": key }));
        } catch (error) {
          const attempt = lostAttempt(key, data, error);
          if (attempt !== undefined) lost.current.set(named, attempt);
          throw error;
        }
      }
      return { assets, folderId: asked.folderId };
    },
    onSuccess: (done, asked) => {
      markSaved(queryClient, teamId, done.assets);
      setSaved((current) => new Set([...current, ...asked.picks.map(outputKey)]));
    },
    onSettled: () => refreshLibraryLists(queryClient, teamId, brandId),
  });
  let result: ReactNode = null;
  if (save.isSuccess) {
    result = (
      <SavedLine
        teamId={teamId}
        brandId={brandId}
        assets={save.data.assets}
        folderId={save.data.folderId}
      />
    );
  } else if (save.isError) {
    result = (
      <Text size="sm" role="alert">
        {problemOf(save.error)}
      </Text>
    );
  }
  return {
    /**
     * Saves these outputs, in the project and under the name the options give; `onSaved` or
     * `onFailed` after. Each call answers its own: TanStack drops a call's `mutate` callbacks once
     * a later save starts or this unmounts (a preview closed), and a tab opened for a save must
     * still go on or close.
     */
    save: (picks: readonly OutputPick[], options: SaveOptions = {}) => {
      void save
        .mutateAsync({ picks, folderId: options.folderId ?? null, name: options.name ?? null })
        .then(
          (done) => options.onSaved?.(done.assets),
          () => options.onFailed?.(),
        );
    },
    saving: save.isPending,
    saved,
    result,
    /** The last save was refused: `result` says why. */
    failed: save.isError,
  };
}
